import { createFileRoute, Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { listServiceCategoriesAdmin, reorderServiceCategories, deleteServiceCategory } from "@/lib/api/services.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/services/")({
  loader: () => listServiceCategoriesAdmin(),
  component: AdminServicesList,
});

type Row = Awaited<ReturnType<typeof listServiceCategoriesAdmin>>[number];

function AdminServicesList() {
  const initial = Route.useLoaderData();
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);

  async function persistOrder(next: Row[]) {
    setBusy(true);
    try {
      await reorderServiceCategories({ data: { ids: next.map((r) => r.id) } });
      setRows(next);
      toast.success("Order updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to reorder");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(row: Row) {
    try {
      await deleteServiceCategory({ data: { id: row.id } });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Category deleted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
      throw e;
    }
  }

  const columns: ColumnDef<Row>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    { accessorKey: "slug", header: "Slug", meta: { filterVariant: "text" } },
    {
      id: "itemCount",
      header: "Items",
      accessorFn: (row) => row.items.length,
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: { filterVariant: "select", groupable: true },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      ...tableActionsColumn<Row>(),
      cell: ({ row }) => (
        <TableRowActions
          edit={{ to: "/admin/services/$id", params: { id: row.original.id } }}
          onDelete={() => handleDelete(row.original)}
          deleteLabel={row.original.name}
        />
      ),
    },
  ];

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Services"
        action={
          <Button asChild>
            <Link to="/admin/services/new">
              <Plus className="mr-2 h-4 w-4" /> New category
            </Link>
          </Button>
        }
      />
      <DataTable
        tableId="services"
        columns={columns}
        data={rows}
        enableRowReorder
        onReorder={persistOrder}
        reorderDisabled={busy}
      />
    </AdminPageShell>
  );
}
